import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    CustomerDTO,
    useSearchCustomerApi,
    useSaveCustomerApi,
    useDeleteCustomerApi,
} from '../../../api/customer-api';
import SvgAdd from '../../../assets/icons/Add';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { FormikCellToggle } from '../../../components/formik';
import { DateCell, ToggleCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveCustomerModal } from './save-customer-modal';
import { LoadingButton } from '../../../components/loading-button';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { ModifiedRowInfo } from '../../../components/table/modified-row-info';
import { CreatedRowInfo } from '../../../components/table/created-row-info';
import { Tel } from '../../../components/basic/tel';

export function CustomerApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | undefined>();
    const customerInit = useSearchCustomerApi();
    const saveCustomer = useSaveCustomerApi();
    const deleteCustomer = useDeleteCustomerApi();

    const { result, error, busy, initialized, initializing } = customerInit;

    const columns: IBasicTableColumn<CustomerDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 40,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>company-name</T>,
                name: 'companyName',
                width: 120,
            },
            {
                Header: <T>national-id</T>,
                name: 'nationalId',
                width: 100,
            },
            {
                Header: <T>display-name</T>,
                name: 'firstName',
                width: 200,
                Cell: ({ row }) => (
                    <bd.Flex align="center" gap={2}>
                        <span>{row.firstName}</span>
                        <span>{row.lastName}</span>
                    </bd.Flex>
                ),
            },
            {
                Header: <T>national-code</T>,
                name: 'nationalCode',
                width: 80,
            },
            {
                Header: <T>phone-number</T>,
                name: 'phoneNumber',
                width: 80,
                Cell: ({ row }) => <Tel>{row.phoneNumber}</Tel>,
            },

            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 80,
                Cell: CreatedRowInfo,
            },
            {
                Header: <T>modified-by</T>,
                name: 'modifiedBy',
                width: 80,
                Cell: ModifiedRowInfo,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        customerInit.post(null, {});
    }, [initialized, initializing, error, customerInit]);

    const handleDelete = (customer: CustomerDTO) => {
        bd.msgbox(
            translate('delete-customer'),
            translate('confirm-delete-customer'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteCustomer.remove(
                            customer,
                            () => {
                                customerInit.updateResult({
                                    ...result,
                                    customers: result.customers.filter(b => b.id !== customer.id),
                                });
                                notify.dark(translate('selected-customer-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedCustomer(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (customer: CustomerDTO) => {
        saveCustomer.post(
            null,
            customer,
            res => {
                if (selectedCustomer) {
                    customerInit.updateResult({
                        ...result,
                        customers: result.customers.map(b =>
                            b.id === customer.id ? res.customer : b
                        ),
                    });
                } else {
                    customerInit.updateResult({
                        ...result,
                        customers: [res.customer, ...result.customers],
                    });
                }

                setShowModal(false);
                setSelectedCustomer(undefined);
                notify.success(
                    selectedCustomer
                        ? translate('selected-customer-updated-successfully')
                        : translate('new-customer-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => customerInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="customer"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-customer</T>}
                            icon={<SvgAdd />}
                            loading={busy}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedCustomer(undefined);
                            }}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.customers}
                        onRowClick={({ row }) => {
                            setSelectedCustomer(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveCustomerModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedCustomer(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedCustomer={selectedCustomer}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
